import { FileText } from "lucide-react"
import { useLang } from "../context/LangContext"
import { LegalPage } from "./LegalShared"

const CONTENT = {
  en: {
    title: "Terms of Service",
    updated: "Last updated: April 2026",
    intro: "By accessing or using uCloud360, you agree to be bound by these Terms of Service. Please read them carefully. If you do not agree, do not use the platform.",
    sections: [
      {
        h: "Description of Service",
        body: "uCloud360 is a digital incubation and acceleration platform offering collaboration tools, educational resources, specialty channels, mentorship programs, and proprietary technology utilities (compound interest calculator, time tracker, coin market tracker, economic calendar) developed by Inteligentic Solutions LLC."
      },
      {
        h: "Account Registration",
        bullets: [
          "You must provide accurate and complete information when creating an account.",
          "You are responsible for maintaining the confidentiality of your credentials.",
          "You must be at least 13 years of age to create an account.",
          "Accounts are non-transferable."
        ]
      },
      {
        h: "Subscriptions & Payments",
        bullets: [
          "Certain features require a paid subscription (Plus or Premium plan).",
          "Payments are processed securely by Stripe. uCloud360 does not store payment card details.",
          "Subscriptions auto-renew unless cancelled before the renewal date.",
          "Refunds are handled on a case-by-case basis — contact info@ucloud360.com."
        ]
      },
      {
        h: "No Professional Advice",
        body: "All content and tools on uCloud360 are provided for informational and educational purposes only. uCloud360 does not provide legal, financial, tax, investment, or other professional advice. Always consult a qualified professional for specific guidance."
      },
      {
        h: "User Conduct",
        bullets: [
          "You agree not to use the platform for any unlawful purpose.",
          "You will not post content that is defamatory, abusive, harassing, or harmful.",
          "You will not attempt to gain unauthorized access to any part of the platform.",
          "uCloud360 reserves the right to suspend or terminate accounts that violate these terms."
        ]
      },
      {
        h: "Intellectual Property",
        body: "All content, tools, designs, and code on uCloud360 are the property of Inteligentic Solutions LLC and Jean Paul Zerpa. The platform name, logo, and concept are protected under US copyright law (Registration PRE000012900). Unauthorized reproduction or distribution is strictly prohibited."
      },
      {
        h: "Limitation of Liability",
        body: 'uCloud360 is provided "as is" without warranty of any kind. Inteligentic Solutions LLC shall not be liable for indirect, incidental, special, or consequential damages arising from your use of the platform, including loss of data, revenue, or business opportunities.'
      },
      {
        h: "Third-Party Services",
        body: "The platform integrates with Stripe (payments), Supabase (authentication and data), CoinGecko, and Investing.com. We are not responsible for the availability, accuracy, or policies of these third-party services."
      },
      {
        h: "Modifications",
        body: "We reserve the right to modify, suspend, or discontinue the service at any time. We will make reasonable efforts to notify users of significant changes."
      },
      {
        h: "Governing Law",
        body: "These Terms are governed by the laws of the State of Florida, United States, without regard to conflict of law provisions."
      },
      {
        h: "Contact",
        body: "For any questions regarding these Terms: info@ucloud360.com | uCloud360 · Inteligentic Solutions LLC · Miami, FL"
      }
    ]
  },
  es: {
    title: "Términos y Condiciones",
    updated: "Última actualización: Abril 2026",
    intro: "Al acceder o usar uCloud360, aceptas estar sujeto a estos Términos y Condiciones. Léelos detenidamente. Si no estás de acuerdo, no uses la plataforma.",
    sections: [
      {
        h: "Descripción del Servicio",
        body: "uCloud360 es una plataforma digital de incubación y aceleración que ofrece herramientas de colaboración, recursos educativos, canales de especialidad, programas de mentoría y utilidades tecnológicas propias (calculadora de interés compuesto, time tracker, rastreador de mercado cripto, calendario económico) desarrolladas por Inteligentic Solutions LLC."
      },
      {
        h: "Registro de Cuenta",
        bullets: [
          "Debes proporcionar información precisa y completa al crear una cuenta.",
          "Eres responsable de mantener la confidencialidad de tus credenciales.",
          "Debes tener al menos 13 años para crear una cuenta.",
          "Las cuentas no son transferibles."
        ]
      },
      {
        h: "Suscripciones y Pagos",
        bullets: [
          "Ciertas funciones requieren una suscripción de pago (plan Plus o Premium).",
          "Los pagos son procesados de forma segura por Stripe. uCloud360 no almacena datos de tarjetas de pago.",
          "Las suscripciones se renuevan automáticamente a menos que se cancelen antes de la fecha de renovación.",
          "Los reembolsos se evalúan caso por caso — escribe a info@ucloud360.com."
        ]
      },
      {
        h: "Sin Asesoría Profesional",
        body: "Todo el contenido y herramientas de uCloud360 se proporcionan únicamente con fines informativos y educativos. uCloud360 no brinda asesoría legal, financiera, fiscal, de inversión ni de ningún otro tipo profesional."
      },
      {
        h: "Conducta del Usuario",
        bullets: [
          "Aceptas no usar la plataforma para ningún propósito ilegal.",
          "No publicarás contenido difamatorio, abusivo, acosador o dañino.",
          "No intentarás acceder sin autorización a ninguna parte de la plataforma.",
          "uCloud360 se reserva el derecho de suspender o cancelar cuentas que violen estos términos."
        ]
      },
      {
        h: "Propiedad Intelectual",
        body: "Todo el contenido, herramientas, diseños y código de uCloud360 son propiedad de Inteligentic Solutions LLC y Jean Paul Zerpa. La plataforma está protegida bajo las leyes de copyright de EE. UU. (Registro PRE000012900). La reproducción o distribución no autorizada está estrictamente prohibida."
      },
      {
        h: "Limitación de Responsabilidad",
        body: 'uCloud360 se proporciona "tal cual" sin garantía de ningún tipo. Inteligentic Solutions LLC no será responsable de daños indirectos, incidentales, especiales o consecuentes derivados del uso de la plataforma.'
      },
      {
        h: "Servicios de Terceros",
        body: "La plataforma integra Stripe (pagos), Supabase (autenticación y datos), CoinGecko e Investing.com. No somos responsables de la disponibilidad, precisión o políticas de estos servicios de terceros."
      },
      {
        h: "Modificaciones",
        body: "Nos reservamos el derecho de modificar, suspender o discontinuar el servicio en cualquier momento. Haremos esfuerzos razonables para notificar a los usuarios sobre cambios significativos."
      },
      {
        h: "Ley Aplicable",
        body: "Estos Términos se rigen por las leyes del Estado de Florida, Estados Unidos."
      },
      {
        h: "Contacto",
        body: "Para preguntas sobre estos Términos: info@ucloud360.com | uCloud360 · Inteligentic Solutions LLC · Miami, FL"
      }
    ]
  }
}

export default function TermsPage() {
  const { lang } = useLang()
  const t = CONTENT[lang]
  return <LegalPage icon={<FileText className="h-5 w-5" />} content={t} />
}
